import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { apiService, Product, Category } from '../services/api';
import { shopService, Shop, ShopProduct } from '../services/shopService';
import { getValidImageUrl } from '../utils/imageUtils';
import { useCategoryTranslation } from '../utils/categoryTranslations';
import { useTranslations } from '../hooks/useTranslations';
import { useLanguage } from '../hooks/useLanguage';
import { useAuth } from '../contexts/AuthContext';
import CustomerHeader from '../components/CustomerHeader';
import { useCartCount } from '../hooks/useCartCount';
import './ShopPage.css';
import '../App.css';

const ShopPage: React.FC = () => {
  const { shopId } = useParams<{ shopId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as any;
  
  // Get optional category filter from navigation state
  const categoryId = locationState?.categoryId;
  const categoryName = locationState?.categoryName;
  const selectedProductId = locationState?.selectedProductId;

  const [shop, setShop] = useState<Shop | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [categoryProducts, setCategoryProducts] = useState<Product[]>([]);
  const [otherProducts, setOtherProducts] = useState<Product[]>([]);
  const [filteredCategoryProducts, setFilteredCategoryProducts] = useState<Product[]>([]);
  const [filteredOtherProducts, setFilteredOtherProducts] = useState<Product[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(true);
  const [cartItems, setCartItems] = useState<Array<{ product: ShopProduct; shop: Shop; quantity: number }>>([]);
  const [sortBy, setSortBy] = useState<'name' | 'price-low' | 'price-high' | 'rating'>('name');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>('all');
  const [availableCategories, setAvailableCategories] = useState<Category[]>([]);
  const [wishlist, setWishlist] = useState<any[]>([]);
  const { isAuthenticated, user } = useAuth();
  
  // Use shared hook for cart count
  const cartCount = useCartCount(500);

  // Load wishlist from API
  useEffect(() => {
    const loadWishlist = async () => {
      if (!isAuthenticated || !user?.id) {
        try {
          const saved = localStorage.getItem('wishlist');
          setWishlist(saved ? JSON.parse(saved) : []);
        } catch {
          setWishlist([]);
        }
        return;
      }

      try {
        const wishlistData = await apiService.getWishlist(user.id);
        setWishlist(wishlistData || []);
      } catch (error) {
        console.error('Error loading wishlist:', error);
        try {
          const saved = localStorage.getItem('wishlist');
          setWishlist(saved ? JSON.parse(saved) : []);
        } catch {
          setWishlist([]);
        }
      }
    };

    loadWishlist();
  }, [isAuthenticated, user?.id]);
  const translateCategoryName = useCategoryTranslation();
  const t = useTranslations();
  const { language } = useLanguage();

  // Load cart items
  useEffect(() => {
    try {
      const storedCart = localStorage.getItem('multiShopCart');
      if (storedCart) {
        const parsedCart = JSON.parse(storedCart);
        setCartItems(parsedCart);
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
    }
  }, []);

  // Save cart items
  useEffect(() => {
    try {
      localStorage.setItem('multiShopCart', JSON.stringify(cartItems));
      // Dispatch cart update event to update header
      window.dispatchEvent(new CustomEvent('cartUpdated'));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [cartItems]);

  // Load shop details
  useEffect(() => {
    if (shopId) {
      loadShopDetails();
      loadShopProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopId, categoryId, categoryName]);

  // Load available categories for filtering
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categories = await apiService.getCategories();
        // Get unique categories from products
        const productCategories = new Set(
          products.map(p => {
            const catId = p.category_id || (p as any).categoryId;
            return catId ? String(catId) : null;
          }).filter(Boolean) as string[]
        );
        const relevantCategories = categories.filter(cat => 
          productCategories.has(String(cat.id))
        );
        setAvailableCategories(relevantCategories);
      } catch (error) {
        console.error('Error loading categories:', error);
      }
    };
    
    if (products.length > 0) {
      loadCategories();
    }
  }, [products]);

  // Filter and sort products based on search query, category filter, and sort option
  const filteredAndSortedProducts = useMemo(() => {
    let filtered = [...products];
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(query) ||
        product.brand?.toLowerCase().includes(query) ||
        product.category?.toLowerCase().includes(query)
      );
    }
    
    // Apply category filter
    if (selectedCategoryFilter !== 'all') {
      filtered = filtered.filter(product => {
        const productCategoryId = product.category_id || (product as any).categoryId;
        return productCategoryId && String(productCategoryId) === selectedCategoryFilter;
      });
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          const nameA = (a.name || '').toLowerCase();
          const nameB = (b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        case 'price-low':
          const priceA = a.price || 0;
          const priceB = b.price || 0;
          return priceA - priceB;
        case 'price-high':
          const priceAHigh = a.price || 0;
          const priceBHigh = b.price || 0;
          return priceBHigh - priceAHigh;
        case 'rating':
          const ratingA = (a as any).rating || 0;
          const ratingB = (b as any).rating || 0;
          return ratingB - ratingA;
        default:
          return 0;
      }
    });
    
    return filtered;
  }, [products, searchQuery, selectedCategoryFilter, sortBy]);

  // Legacy filter logic for category-based view (when categoryId is provided)
  useEffect(() => {
    if (categoryId && categoryName) {
      if (searchQuery.trim() === '') {
        setFilteredCategoryProducts(categoryProducts);
        setFilteredOtherProducts(otherProducts);
      } else {
        const query = searchQuery.toLowerCase();
        const filteredCat = categoryProducts.filter(product =>
          product.name.toLowerCase().includes(query) ||
          product.brand?.toLowerCase().includes(query) ||
          product.category?.toLowerCase().includes(query)
        );
        const filteredOther = otherProducts.filter(product =>
          product.name.toLowerCase().includes(query) ||
          product.brand?.toLowerCase().includes(query) ||
          product.category?.toLowerCase().includes(query)
        );
        setFilteredCategoryProducts(filteredCat);
        setFilteredOtherProducts(filteredOther);
      }
    }
  }, [searchQuery, categoryProducts, otherProducts, categoryId, categoryName]);

  const loadShopDetails = async () => {
    if (!shopId) {
      console.error('❌ ShopPage: No shopId provided');
      return;
    }
    
    const normalizedShopId = String(shopId).trim();
    console.log('🛍️ ShopPage: Loading shop details for ID:', normalizedShopId);
    
    try {
      setLoading(true);
      // Get shops list and find the shop (matching mobile app logic)
      const shops = await apiService.getShops();
      console.log('📦 ShopPage: Loaded shops list, total:', shops.length);
      
      // Try to find shop by exact ID match
      let foundShop = shops.find(s => String(s.id).trim() === normalizedShopId);
      
      // If not found, try case-insensitive match
      if (!foundShop) {
        foundShop = shops.find(s => String(s.id).trim().toLowerCase() === normalizedShopId.toLowerCase());
      }
      
      if (foundShop) {
        // Normalize shop data
        const normalizedShop: Shop = {
          ...foundShop,
          logo: getValidImageUrl(foundShop.logo || foundShop.logo_url || foundShop.image || foundShop.image_url || foundShop.photo || ''),
        };
        setShop(normalizedShop);
        console.log('✅ ShopPage: Shop loaded successfully:', {
          name: normalizedShop.name,
          id: normalizedShop.id,
          type: normalizedShop.type
        });
      } else {
        console.warn('⚠️ ShopPage: Shop not found in API shops list, trying shopService fallback');
        // Fallback: try shopService
        try {
          const shopData = await shopService.getShop(normalizedShopId);
          setShop(shopData);
          console.log('✅ ShopPage: Shop loaded from shopService:', shopData.name);
        } catch (error) {
          console.error('❌ ShopPage: Shop not found in shopService:', error);
          // Don't redirect - show error state instead
        }
      }
    } catch (error) {
      console.error('❌ ShopPage: Error loading shop:', error);
      // Don't redirect - show error state instead
    } finally {
      setLoading(false);
    }
  };

  const loadShopProducts = async () => {
    if (!shopId) return;

    try {
      setProductsLoading(true);
      console.log('🛍️ Loading products for shop ID:', shopId);
      if (categoryId && categoryName) {
        console.log('📋 Category filter active - Category ID:', categoryId, 'Name:', categoryName);
      }

      // Use API service to get shop products (matching mobile app)
      const shopProducts = await apiService.getShopProducts(shopId, {
        category: categoryId || undefined
      });
      console.log('✅ Shop products loaded from API:', shopProducts?.length || 0);

      if (shopProducts && shopProducts.length > 0) {
        // Validate products belong to this shop (matching mobile app logic)
        const validatedProducts = shopProducts.filter((product: any) => {
          const productShopId = product.shop_id || product.shopId || (product.shop && product.shop.id);
          const productTenantId = product.tenant_id || product.tenantId;
          const shopTenantId = shop?.id || shopId;

          const matchesShopId = productShopId && (productShopId.toString() === shopId.toString());
          const matchesTenantId = productTenantId && (
            productTenantId.toString() === shopId.toString() ||
            productTenantId.toString() === shopTenantId.toString()
          );

          return matchesShopId || matchesTenantId;
        });

        console.log('🔍 Validated products:', validatedProducts.length, 'out of', shopProducts.length);

        if (validatedProducts.length > 0) {
          // If category filter is provided, separate products by category
          if (categoryId && categoryName) {
            try {
              const categories = await apiService.getCategories();
              const category = categories.find(cat => cat.id === categoryId);
              const categoryNameToMatch = category?.name || categoryName;

              const filteredByCategory = validatedProducts.filter((product: any) => {
                const productCategoryId = product.category_id || product.categoryId;
                const productCategory = product.category || product.category_name;

                if (productCategoryId && productCategoryId.toString() === categoryId.toString()) {
                  return true;
                }

                if (productCategory) {
                  const productCategoryLower = productCategory.toLowerCase();
                  const categoryNameLower = categoryNameToMatch.toLowerCase();
                  return productCategoryLower === categoryNameLower ||
                    productCategoryLower.includes(categoryNameLower) ||
                    categoryNameLower.includes(productCategoryLower);
                }

                return false;
              });

              const categoryProductIds = new Set(filteredByCategory.map((p: any) => p.id));
              const other = validatedProducts.filter((product: any) => {
                return !categoryProductIds.has(product.id);
              });

              console.log('✅ Category products:', filteredByCategory.length);
              console.log('✅ Other products:', other.length);

              setCategoryProducts(filteredByCategory);
              setOtherProducts(other);
              setFilteredCategoryProducts(filteredByCategory);
              setFilteredOtherProducts(other);
              setProducts(validatedProducts);
            } catch (filterError) {
              console.error('❌ Error filtering products by category:', filterError);
              setProducts(validatedProducts);
              setCategoryProducts([]);
              setOtherProducts([]);
            }
          } else {
            // No category filter - show all validated products
            setProducts(validatedProducts);
            setCategoryProducts([]);
            setOtherProducts([]);
            setFilteredCategoryProducts([]);
            setFilteredOtherProducts([]);
          }
        } else {
          setProducts([]);
          setCategoryProducts([]);
          setOtherProducts([]);
        }
      } else {
        console.warn('⚠️ No products returned for shop:', shopId);
        setProducts([]);
        setCategoryProducts([]);
        setOtherProducts([]);
      }
    } catch (error: any) {
      console.error('❌ Error loading shop products:', error);
      setProducts([]);
      setCategoryProducts([]);
      setOtherProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  const getCartQuantity = (productId: string): number => {
    const item = cartItems.find(item => 
      item.product.id === productId && item.shop.id === shopId
    );
    return item?.quantity || 0;
  };

  const addToCart = (product: Product) => {
    if (!shop) return;

    const existingItem = cartItems.find(item => 
      item.product.id === product.id && item.shop.id === shop.id
    );

    if (existingItem) {
      setCartItems(prev =>
        prev.map(item =>
          item.product.id === product.id && item.shop.id === shop.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    } else {
      const shopProduct: ShopProduct = {
        ...product,
        id: String(product.id),
        shopId: shop.id,
        category_id: product.category_id,
        stock: (product as any).stock_quantity || 0,
        inStock: product.inStock !== undefined ? product.inStock : true,
        createdAt: product.created_at || new Date().toISOString(),
        updatedAt: product.updated_at || new Date().toISOString()
      };
      setCartItems(prev => [...prev, { product: shopProduct, shop, quantity: 1 }]);
    }
  };

  const updateQuantity = (product: Product, newQuantity: number) => {
    if (!shop) return;

    if (newQuantity < 1) {
      setCartItems(prev =>
        prev.filter(item => !(item.product.id === product.id && item.shop.id === shop.id))
      );
    } else {
      setCartItems(prev =>
        prev.map(item =>
          item.product.id === product.id && item.shop.id === shop.id
            ? { ...item, quantity: newQuantity }
            : item
        )
      );
    }
  };

  // Wishlist functions
  const isInWishlist = (productId: string): boolean => {
    return wishlist.some(item => String(item.id) === String(productId));
  };

  const handleAddToWishlist = async (product: Product) => {
    if (!isAuthenticated || !user?.id) {
      // Fallback to localStorage for guests
      if (isInWishlist(String(product.id))) {
        const updated = wishlist.filter(item => String(item.id) !== String(product.id));
        setWishlist(updated);
        localStorage.setItem('wishlist', JSON.stringify(updated));
        window.dispatchEvent(new CustomEvent('wishlistUpdated'));
      } else {
        const wishlistItem = {
          id: product.id,
          name: product.name,
          price: product.price,
          originalPrice: (product as any).originalPrice || (product as any).original_price,
          discount: product.discount || 0,
          image: product.image || '📦',
          rating: (product as any).rating || 0,
          brand: product.brand || '',
          weight: (product as any).weight || '',
          category: product.category || ''
        };
        const updated = [...wishlist, wishlistItem];
        setWishlist(updated);
        localStorage.setItem('wishlist', JSON.stringify(updated));
        window.dispatchEvent(new CustomEvent('wishlistUpdated'));
      }
      return;
    }

    try {
      if (isInWishlist(String(product.id))) {
        await apiService.removeFromWishlist(user.id, String(product.id));
        const updated = wishlist.filter(item => String(item.id) !== String(product.id));
        setWishlist(updated);
      } else {
        const productData = {
          name: product.name,
          price: product.price,
          originalPrice: (product as any).originalPrice || (product as any).original_price,
          discount: product.discount || 0,
          image: product.image || '📦',
          rating: (product as any).rating || 0,
          brand: product.brand || '',
          weight: (product as any).weight || '',
          category: product.category || ''
        };
        await apiService.addToWishlist(user.id, String(product.id), productData);
        const wishlistItem = { id: product.id, ...productData };
        const updated = [...wishlist, wishlistItem];
        setWishlist(updated);
      }
      window.dispatchEvent(new CustomEvent('wishlistUpdated'));
    } catch (error) {
      console.error('Error updating wishlist:', error);
      // Fallback to localStorage on error
      if (isInWishlist(String(product.id))) {
        const updated = wishlist.filter(item => String(item.id) !== String(product.id));
        setWishlist(updated);
        localStorage.setItem('wishlist', JSON.stringify(updated));
      } else {
        const wishlistItem = {
          id: product.id,
          name: product.name,
          price: product.price,
          originalPrice: (product as any).originalPrice || (product as any).original_price,
          discount: product.discount || 0,
          image: product.image || '📦',
          rating: (product as any).rating || 0,
          brand: product.brand || '',
          weight: (product as any).weight || '',
          category: product.category || ''
        };
        const updated = [...wishlist, wishlistItem];
        setWishlist(updated);
        localStorage.setItem('wishlist', JSON.stringify(updated));
      }
      window.dispatchEvent(new CustomEvent('wishlistUpdated'));
    }
  };

  const renderProduct = (product: Product) => {
    const quantity = getCartQuantity(String(product.id));
    const inCart = quantity > 0;
    const isSelected = selectedProductId && product.id === selectedProductId;

    return (
      <div key={product.id} className={`product-card ${isSelected ? 'highlighted' : ''}`}>
        <div
          className="product-content"
          onClick={() => {
            // Navigate to product details (if available)
            console.log('🛍️ Product clicked:', product.id, product.name);
          }}
        >
          {product.discount && product.discount > 0 && (
            <div className="discount-badge">{product.discount}% OFF</div>
          )}
          {isSelected && (
            <div className="selected-badge">
              <span>✓</span> Selected
            </div>
          )}
          <div className="product-image">
            {product.image && !product.image.startsWith('📦') && !product.image.startsWith('🛍️') ? (
              <img src={getValidImageUrl(product.image)} alt={product.name} />
            ) : (
              <span className="product-emoji">📦</span>
            )}
            <button
              className={`wishlist-btn ${isInWishlist(String(product.id)) ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                handleAddToWishlist(product);
              }}
              aria-label={isInWishlist(String(product.id)) ? 'Remove from wishlist' : 'Add to wishlist'}
              title={isInWishlist(String(product.id)) ? 'Remove from wishlist' : 'Add to wishlist'}
            >
              {isInWishlist(String(product.id)) ? '❤️' : '🤍'}
            </button>
          </div>
          <div className="product-info">
            <div className="product-name">{product.name}</div>
            <div className="price-container">
              {product.discount && product.discount > 0 ? (
                <>
                  <span className="product-price">
                    ₹{Math.round(product.price * (1 - product.discount / 100))}
                  </span>
                  <span className="original-price">₹{product.price}</span>
                </>
              ) : (
                <span className="product-price">₹{product.price}</span>
              )}
            </div>
            <div className="product-brand">{product.brand || product.category}</div>
            <div className="stock-badge">
              <span>✓</span> In Stock
            </div>
          </div>
        </div>

        {/* Add to Cart Button or Quantity Controls */}
        {!inCart ? (
          <button
            className="add-to-cart-button"
            onClick={(e) => {
              e.stopPropagation();
              addToCart(product);
            }}
          >
            Add to Cart
          </button>
        ) : (
          <div className="quantity-controls">
            <button
              className="quantity-button"
              onClick={(e) => {
                e.stopPropagation();
                updateQuantity(product, quantity - 1);
              }}
            >
              −
            </button>
            <span className="quantity-text">{quantity}</span>
            <button
              className="quantity-button"
              onClick={(e) => {
                e.stopPropagation();
                updateQuantity(product, quantity + 1);
              }}
            >
              +
            </button>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="shop-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading shop...</p>
        </div>
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="shop-page">
        <div className="error-container">
          <div className="error-icon">🏪</div>
          <h2>Shop not found</h2>
          <button className="back-button" onClick={() => navigate('/app')}>
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="shop-page">
      {/* Header - Using CustomerHeader component for consistency */}
      <CustomerHeader
        variant="sticky"
        showTagline
        showVoiceSearch
        showProfileDropdown
        showBackButton={true}
        showLoyaltyButton
        cartCount={cartCount}
        onBackClick={() => navigate('/app')}
        onSearch={(query) => {
          navigate(`/search?q=${encodeURIComponent(query)}`);
        }}
        onCartClick={() => navigate('/app', { state: { showCart: true } })}
      />

      {/* Breadcrumbs */}
      <nav className="breadcrumbs" aria-label="Breadcrumb" style={{ padding: '16px 20px', maxWidth: '1320px', margin: '0 auto' }}>
        <button 
          className="breadcrumb-link"
          onClick={() => navigate('/app')}
        >
          Dashboard
        </button>
        <span className="breadcrumb-separator">›</span>
        <button 
          className="breadcrumb-link"
          onClick={() => navigate('/explore-shops')}
        >
          Explore Shops
        </button>
        {shop && (
          <>
            <span className="breadcrumb-separator">›</span>
            <span className="breadcrumb-current">{shop.name}</span>
          </>
        )}
      </nav>

      {/* Navigation Buttons */}
      <nav id="navigation" className="main-nav" role="navigation" aria-label="Primary">
        <div className="nav-content">
          <div className="nav-buttons">
            <button 
              className={`nav-btn ${location.pathname === '/app' || location.pathname === '/' || location.pathname === '/customer' || location.pathname === '/customer/home' ? 'active' : ''}`} 
              onClick={() => navigate('/app')}
            >
              🏠 {t.mainStore || 'Dashboard'}
            </button>
            <button 
              className={`nav-btn ${location.pathname === '/explore-shops' || location.pathname === '/shops' ? 'active' : ''}`} 
              onClick={() => navigate('/explore-shops')}
            >
              🔎 {t.exploreShops || 'Explore Shops'}
            </button>
            <button 
              className={`nav-btn ${location.pathname === '/browse-all' || location.pathname === '/browse' ? 'active' : ''}`} 
              onClick={() => navigate('/browse-all')}
            >
              🛍️ {t.browseAll || 'Browse All'}
            </button>
            <button 
              className={`nav-btn ${location.pathname === '/services' || location.pathname.startsWith('/services/') ? 'active' : ''}`} 
              onClick={() => navigate('/services')}
            >
              🔧 {language === 'hi' ? 'सेवाएं' : language === 'te' ? 'సేవలు' : language === 'ta' ? 'சேவைகள்' : 'Services'}
            </button>
          </div>
        </div>
      </nav>

      {/* Shop Header with Gradient */}
      <div 
        className="shop-header-gradient"
        style={{
          background: shop.theme_config?.primaryColor 
            ? `linear-gradient(135deg, ${shop.theme_config.primaryColor} 0%, ${shop.theme_config.secondaryColor || shop.theme_config.primaryColor} 100%)`
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}
      >
        <div className="shop-header-content">
          <div className="shop-logo-container">
            {shop.logo ? (
              <img src={getValidImageUrl(shop.logo)} alt={shop.name} className="shop-logo" />
            ) : (
              <div className="shop-logo-fallback">🏪</div>
            )}
          </div>
          <h1 className="shop-name">{shop.name}</h1>
          {shop.owner && (
            <p className="shop-owner">
              Shop owned by {shop.owner}
            </p>
          )}
          {shop.description && (
            <p className="shop-description">{shop.description}</p>
          )}
          <div className="shop-meta">
            <div className="rating-container">
              <span className="rating-star">⭐</span>
              <span className="rating-text">
                {shop.rating ? shop.rating.toFixed(1) : '4.5'}
              </span>
              <span className="reviews-text">
                ({shop.total_reviews || 10} {t.reviews || 'reviews'})
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter Bar - Always Visible for Better UX */}
      {!productsLoading && products.length > 0 && (
        <div className="shop-search-filter-bar">
          <div className="body-container">
            <div className="shop-search-wrapper">
              <div className="search-input-container">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  className="shop-search-input"
                  placeholder="Search products in this shop..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search products"
                />
                {searchQuery.length > 0 && (
                  <button
                    className="clear-search-button"
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>
              
              <div className="shop-filter-controls">
                {/* Category Filter - Only show if categories available */}
                {!categoryId && availableCategories.length > 0 && (
                  <select
                    className="category-filter-select"
                    value={selectedCategoryFilter}
                    onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                    aria-label="Filter by category"
                  >
                    <option value="all">All Categories</option>
                    {availableCategories.map(cat => {
                      const catId = cat.id;
                      const count = products.filter(p => {
                        const productCategoryId = p.category_id || (p as any).categoryId;
                        return productCategoryId && String(productCategoryId) === String(catId);
                      }).length;
                      return (
                        <option key={catId} value={catId}>
                          {translateCategoryName(cat.name || cat.display_name || '')} ({count})
                        </option>
                      );
                    })}
                  </select>
                )}
                
                {/* Sort Options - Only show when not in category view */}
                {!categoryId && (
                  <select
                    className="sort-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as 'name' | 'price-low' | 'price-high' | 'rating')}
                    aria-label="Sort products"
                  >
                    <option value="name">Sort: Name (A-Z)</option>
                    <option value="price-low">Sort: Price (Low to High)</option>
                    <option value="price-high">Sort: Price (High to Low)</option>
                    <option value="rating">Sort: Rating (High to Low)</option>
                  </select>
                )}
              </div>
            </div>
            
            {/* Results Count */}
            {!categoryId && (searchQuery || selectedCategoryFilter !== 'all') && (
              <div className="shop-results-count">
                <span>{filteredAndSortedProducts.length} {filteredAndSortedProducts.length === 1 ? 'product' : 'products'} found</span>
                <button
                  className="clear-filters-link"
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategoryFilter('all');
                  }}
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Shop Info - Compact Card Design */}
      {(shop.address || shop.phone || shop.email) && (
        <div className="body-container">
          <div className="shop-info-card">
            <h3 className="shop-info-title">Shop Information</h3>
            <div className="shop-info-grid">
              {shop.address && (
                <div className="shop-info-item">
                  <span className="info-icon">📍</span>
                  <div className="info-content">
                    <span className="info-label">Address</span>
                    <span className="info-text">{shop.address}</span>
                  </div>
                </div>
              )}
              {shop.phone && (
                <div className="shop-info-item">
                  <span className="info-icon">📞</span>
                  <div className="info-content">
                    <span className="info-label">Phone</span>
                    <a href={`tel:${shop.phone}`} className="info-text info-link">{shop.phone}</a>
                  </div>
                </div>
              )}
              {shop.email && (
                <div className="shop-info-item">
                  <span className="info-icon">✉️</span>
                  <div className="info-content">
                    <span className="info-label">Email</span>
                    <a href={`mailto:${shop.email}`} className="info-text info-link">{shop.email}</a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Products Section */}
      <div className="products-section">
        <div className="body-container">
          {categoryId && categoryName ? (
            <>
              {/* Category Products Section */}
              {filteredCategoryProducts.length > 0 && (
                <>
                  <div className="products-section-header">
                    <h3 className="section-title">
                      {translateCategoryName(categoryName)} ({filteredCategoryProducts.length})
                    </h3>
                  </div>
                  {productsLoading ? (
                    <div className="loading-state">Loading products...</div>
                  ) : (
                    <div className="products-grid">
                      {filteredCategoryProducts.map(product => renderProduct(product))}
                    </div>
                  )}
                </>
              )}

              {/* All Products Section */}
              {(filteredOtherProducts.length > 0 || (!productsLoading && filteredCategoryProducts.length > 0)) && (
                <>
                  <div className="products-section-header" style={{ marginTop: filteredCategoryProducts.length > 0 ? '32px' : '0' }}>
                    <h3 className="section-title">
                      All Products ({filteredOtherProducts.length > 0 ? filteredOtherProducts.length : (products.length - filteredCategoryProducts.length)})
                    </h3>
                  </div>
                  {productsLoading ? (
                    <div className="loading-state">Loading products...</div>
                  ) : (
                    <div className="products-grid">
                      {(filteredOtherProducts.length > 0 ? filteredOtherProducts : products.filter(p => !filteredCategoryProducts.some(cp => cp.id === p.id))).map(product => renderProduct(product))}
                    </div>
                  )}
                </>
              )}

              {/* Empty state */}
              {!productsLoading && filteredCategoryProducts.length === 0 && filteredOtherProducts.length === 0 && (
                <div className="empty-products">
                  <div className="empty-icon">📦</div>
                  <p className="empty-text">
                    {searchQuery ? 'No products found matching your search' : 'No products available'}
                  </p>
                  {searchQuery && (
                    <button
                      className="clear-search-btn"
                      onClick={() => setSearchQuery('')}
                    >
                      Clear Search
                    </button>
                  )}
                </div>
              )}
            </>
          ) : (
            <>
              {/* No category filter - show all products with filters */}
              <div className="products-section-header">
                <h3 className="section-title">
                  Products {products.length > 0 && `(${filteredAndSortedProducts.length}${searchQuery || selectedCategoryFilter !== 'all' ? ' filtered' : ''})`}
                </h3>
              </div>
              {productsLoading ? (
                <div className="loading-state">Loading products...</div>
              ) : filteredAndSortedProducts.length === 0 ? (
                <div className="empty-products">
                  <div className="empty-icon">📦</div>
                  <p className="empty-text">
                    {searchQuery || selectedCategoryFilter !== 'all' 
                      ? 'No products found matching your filters' 
                      : 'No products available'}
                  </p>
                  {(searchQuery || selectedCategoryFilter !== 'all') && (
                    <button
                      className="clear-search-btn"
                      onClick={() => {
                        setSearchQuery('');
                        setSelectedCategoryFilter('all');
                      }}
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              ) : (
                <div className="products-grid">
                  {filteredAndSortedProducts.map(product => renderProduct(product))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShopPage;

